import React from "react";
import {
  MagnifyingGlassIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import cn from "classnames";

export type ViewMode = "grid" | "list";

export type SortState<K extends string = string> = {
  key: K;
  dir: "asc" | "desc";
};

/** Toggle direction on repeat clicks; switch key with the given default direction. */
export function nextSort<K extends string>(
  current: SortState<K>,
  key: K,
  defaultDir: "asc" | "desc" = "asc"
): SortState<K> {
  if (current.key === key) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: defaultDir };
}

/** Search input + grid/list segmented toggle shown above tile and list views. */
export const ListViewToolbar: React.FC<{
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  // Omit both to hide the tile/list toggle (lists with only a table view).
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  // Filter controls (dropdowns), shown beside the search box.
  children?: React.ReactNode;
}> = ({
  search,
  onSearchChange,
  searchPlaceholder = "Search...",
  viewMode,
  onViewModeChange,
  children,
}) => (
  <div className="flex items-center gap-3 flex-wrap">
    <div className="relative flex-1 max-w-sm">
      <MagnifyingGlassIcon className="h-4 w-4 text-base-content/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        type="text"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        className="w-full h-10 pl-9 pr-8 py-0 text-sm bg-base-100 border border-base-300 rounded-lg outline-none focus:border-primary/50 transition-colors"
      />
      {search && (
        <button
          onClick={() => onSearchChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
          title="Clear search"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </div>
    {children}
    {viewMode && onViewModeChange && (
    <div className="flex items-center border border-base-300 rounded-lg overflow-hidden flex-shrink-0">
      <button
        onClick={() => onViewModeChange("grid")}
        className={cn(
          "p-2 transition-colors",
          viewMode === "grid"
            ? "bg-primary/10 text-primary"
            : "text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 hover:bg-base-200"
        )}
        title="Tile view"
      >
        <Squares2X2Icon className="h-4 w-4" />
      </button>
      <button
        onClick={() => onViewModeChange("list")}
        className={cn(
          "p-2 transition-colors border-l border-base-300",
          viewMode === "list"
            ? "bg-primary/10 text-primary"
            : "text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 hover:bg-base-200"
        )}
        title="List view"
      >
        <ListBulletIcon className="h-4 w-4" />
      </button>
    </div>
    )}
  </div>
);

/** Clickable table header cell with an asc/desc indicator. */
export function SortableTh<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  defaultDir = "asc",
  className,
  resizer,
}: {
  label: string;
  sortKey: K;
  sort: SortState<K>;
  onSort: (sort: SortState<K>) => void;
  defaultDir?: "asc" | "desc";
  className?: string;
  // A <ColumnResizer /> for resizable tables.
  resizer?: React.ReactNode;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      className={cn(
        "relative cursor-pointer select-none hover:text-gray-700 dark:hover:text-base-content transition-colors",
        active && "text-gray-700 dark:text-base-content/80",
        className
      )}
      onClick={() => onSort(nextSort(sort, sortKey, defaultDir))}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active &&
          (sort.dir === "asc" ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : (
            <ChevronDownIcon className="h-3 w-3" />
          ))}
      </span>
      {resizer}
    </th>
  );
}
